import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  StyledSelect,
  StyledMenuItem,
} from '../CompanyInformation/StyledSelect';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Template, TemplateItem } from './hooks/useOrgTemplates';
import { FindingType, FindingTypeTask } from './hooks/useOrgFindingTypes';
import { Zone } from './hooks/useOrgZones';
import useTemplateMutations from './hooks/useTemplateMutations';
import TemplatePublishingFlow from './TemplatePublishingFlow';
import ZoneEditor from './ZoneEditor';
import { dialogPaperProps } from './darkModeStyles';

// ── Props ──

interface TemplateEditorProps {
  templates: Template[];
  findingTypes: FindingType[];
  zones: Zone[];
  onRefreshTemplates: () => void;
  onRefreshFindingTypes: () => void;
  onRefreshZones: () => void;
  isPaivcamSeed?: boolean;
  initialTemplateId?: string | null;
  onBackToList?: () => void;
}

// ── Form types ──

interface NewTaskForm {
  name: string;
  description: string;
  taskType: string;
  zoneId: string;
}

interface FTFormData {
  name: string;
  description: string;
}

interface NewFTTaskForm {
  name: string;
  description: string;
  taskType: string;
  zoneId: string;
}

const EMPTY_TASK_FORM: NewTaskForm = {
  name: '',
  description: '',
  taskType: 'photo',
  zoneId: '',
};
const EMPTY_FT_FORM: FTFormData = { name: '', description: '' };
const EMPTY_FT_TASK_FORM: NewFTTaskForm = {
  name: '',
  description: '',
  taskType: 'photo',
  zoneId: '',
};

// ── Helpers ──

function getStatusColor(
  status: string
): 'success' | 'warning' | 'default' | 'info' {
  if (status === 'published') return 'success';
  if (status === 'draft') return 'warning';
  if (status === 'system') return 'info';
  return 'warning';
}

function getDisplayStatus(status: string): string {
  return status === 'published' ? 'Published' : 'Unpublished';
}

function generateCode(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// ════════════════════════════════════════════════════════════════════════════
// Sortable Template Item Row (used in Checklist tab)
// ════════════════════════════════════════════════════════════════════════════

interface SortableItemRowProps {
  item: TemplateItem;
  zones: Zone[];
  onAssignZone: (itemId: string, zoneId: string) => void;
  onRemove: (itemId: string) => void;
  onEdit: (item: TemplateItem) => void;
  disabled: boolean;
}

function SortableItemRow({
  item,
  zones,
  onAssignZone,
  onRemove,
  onEdit,
  disabled,
}: SortableItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        borderBottom: `1px solid ${theme.custom?.stroke}`,
        backgroundColor: isDragging
          ? theme.custom?.isDark
            ? 'rgba(255, 106, 0, 0.15)'
            : '#FFF3E0'
          : 'transparent',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': {
          backgroundColor: theme.custom?.background2,
        },
      })}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon
          sx={(theme) => ({
            fontSize: 18,
            color: theme.custom?.textLight,
          })}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
        onClick={() => onEdit(item)}
      >
        <Typography
          sx={(theme) => ({
            fontSize: 14,
            fontWeight: 500,
            color: theme.custom?.text,
          })}
        >
          {item.name}
        </Typography>
        {item.description && (
          <Typography
            sx={(theme) => ({
              fontSize: 12,
              color: theme.custom?.textLight,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {item.description}
          </Typography>
        )}
      </Box>
      <Chip
        label={item.taskType}
        size="small"
        variant="outlined"
        sx={(theme) => ({
          flexShrink: 0,
          color: theme.custom?.textLight,
          borderColor: theme.custom?.stroke,
        })}
      />
      <StyledSelect
        size="small"
        value={item.zoneId ?? ''}
        onChange={(e) => onAssignZone(item.id, e.target.value as string)}
        displayEmpty
        disabled={disabled}
        sx={{ minWidth: 140, flexShrink: 0 }}
      >
        <StyledMenuItem value="">
          <em>None</em>
        </StyledMenuItem>
        {zones.map((z) => (
          <StyledMenuItem key={z.id} value={z.id}>
            {z.name}
          </StyledMenuItem>
        ))}
      </StyledSelect>
      <IconButton
        size="small"
        onClick={() => onRemove(item.id)}
        disabled={disabled}
        color="error"
        aria-label="Remove task"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Zone Group (items grouped under a zone header, used in Checklist tab)
// ════════════════════════════════════════════════════════════════════════════

interface ZoneGroupProps {
  zoneKey: string;
  zoneName: string;
  items: TemplateItem[];
  zones: Zone[];
  sensors: ReturnType<typeof useSensors>;
  onItemDragEnd: (zoneKey: string, event: DragEndEvent) => void;
  onAssignZone: (itemId: string, zoneId: string) => void;
  onRemove: (itemId: string) => void;
  onEditItem: (item: TemplateItem) => void;
  disabled: boolean;
  isReadOnly: boolean;
}

function ZoneGroup({
  zoneKey,
  zoneName,
  items,
  zones,
  sensors,
  onItemDragEnd,
  onAssignZone,
  onRemove,
  onEditItem,
  disabled,
  isReadOnly,
}: ZoneGroupProps) {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        mb: 2,
        borderRadius: '10px',
        overflow: 'hidden',
        borderColor: theme.custom?.stroke,
        backgroundColor: theme.custom?.background1,
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          backgroundColor: theme.custom?.background2,
          borderBottom: `1px solid ${theme.custom?.stroke}`,
        })}
      >
        <Typography
          sx={(theme) => ({
            fontSize: 14,
            fontWeight: 600,
            color: theme.custom?.text,
          })}
        >
          {zoneName}
        </Typography>
        <Chip
          label={`${items.length} tasks`}
          size="small"
          variant="outlined"
          sx={(theme) => ({
            color: theme.custom?.textLight,
            borderColor: theme.custom?.stroke,
          })}
        />
      </Box>

      {/* Column headers for editable rows */}
      {!isReadOnly && items.length > 0 && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.75,
            borderBottom: `1px solid ${theme.custom?.stroke}`,
          })}
        >
          <Box sx={{ width: 18 }} /> {/* drag handle spacer */}
          <Typography
            sx={(theme) => ({
              flex: 1,
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: theme.custom?.textLight,
              letterSpacing: 0.5,
            })}
          >
            Task
          </Typography>
          <Typography
            sx={(theme) => ({
              width: 80,
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: theme.custom?.textLight,
              letterSpacing: 0.5,
              flexShrink: 0,
              textAlign: 'center',
            })}
          >
            Type
          </Typography>
          <Typography
            sx={(theme) => ({
              minWidth: 140,
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: theme.custom?.textLight,
              letterSpacing: 0.5,
              flexShrink: 0,
              textAlign: 'center',
            })}
          >
            Zone
          </Typography>
          <Box sx={{ width: 32 }} /> {/* delete button spacer */}
        </Box>
      )}

      {isReadOnly ? (
        items.map((item) => (
          <Box
            key={item.id}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              borderBottom: `1px solid ${theme.custom?.stroke}`,
              '&:last-child': { borderBottom: 'none' },
            })}
          >
            <Typography
              sx={(theme) => ({
                fontSize: 14,
                color: theme.custom?.text,
                flex: 1,
              })}
            >
              {item.name}
            </Typography>
            <Chip
              label={item.taskType}
              size="small"
              variant="outlined"
              sx={(theme) => ({
                color: theme.custom?.textLight,
                borderColor: theme.custom?.stroke,
              })}
            />
          </Box>
        ))
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => onItemDragEnd(zoneKey, event)}
        >
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableItemRow
                key={item.id}
                item={item}
                zones={zones}
                onAssignZone={onAssignZone}
                onRemove={onRemove}
                onEdit={onEditItem}
                disabled={disabled}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {items.length === 0 && (
        <Box px={2} py={2}>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
          >
            No tasks in this zone.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sortable Finding Type Task Row (reused from FindingTypeEditor pattern)
// ════════════════════════════════════════════════════════════════════════════

interface SortableFTTaskRowProps {
  task: FindingTypeTask;
  sortableId: string;
  zones: Zone[];
  onRemove: () => void;
  onEdit: () => void;
  onZoneChange: (zoneId: string | null) => void;
  disabled: boolean;
}

function SortableFTTaskRow({
  task,
  sortableId,
  zones,
  onRemove,
  onEdit,
  onZoneChange,
  disabled,
}: SortableFTTaskRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        borderBottom: `1px solid ${theme.custom?.stroke}`,
        backgroundColor: isDragging
          ? theme.custom?.isDark
            ? 'rgba(255, 106, 0, 0.15)'
            : '#FFF3E0'
          : 'transparent',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': {
          backgroundColor: theme.custom?.background2,
        },
      })}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon
          sx={(theme) => ({
            fontSize: 18,
            color: theme.custom?.textLight,
          })}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
        onClick={onEdit}
      >
        <Typography
          sx={(theme) => ({
            fontSize: 14,
            fontWeight: 500,
            color: theme.custom?.text,
          })}
        >
          {task.name}
        </Typography>
        {task.description && (
          <Typography
            sx={(theme) => ({
              fontSize: 12,
              color: theme.custom?.textLight,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {task.description}
          </Typography>
        )}
      </Box>
      <Chip
        label={task.taskType}
        size="small"
        variant="outlined"
        sx={(theme) => ({
          color: theme.custom?.textLight,
          borderColor: theme.custom?.stroke,
        })}
      />
      <StyledSelect
        size="small"
        value={task.zoneId ?? ''}
        onChange={(e) => onZoneChange((e.target.value as string) || null)}
        displayEmpty
        disabled={disabled}
        sx={{ minWidth: 140, flexShrink: 0 }}
      >
        <StyledMenuItem value="">
          <em>None</em>
        </StyledMenuItem>
        {zones.map((z) => (
          <StyledMenuItem key={z.id} value={z.id}>
            {z.name}
          </StyledMenuItem>
        ))}
      </StyledSelect>
      <IconButton
        size="small"
        onClick={onRemove}
        disabled={disabled}
        color="error"
        aria-label="Remove task"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Finding Type Accordion (used in Damage Types / Property Features tabs)
// ════════════════════════════════════════════════════════════════════════════

interface FindingTypeAccordionProps {
  ft: FindingType;
  isLinked: boolean;
  isTemplateReadOnly: boolean;
  zones: Zone[];
  onToggleLink: (ftId: string, currentlyLinked: boolean) => void;
  onAddTask: (ft: FindingType) => void;
  onRemoveTask: (ft: FindingType, taskIndex: number) => void;
  onEditTask: (ft: FindingType, taskIndex: number) => void;
  onZoneChangeTask: (
    ft: FindingType,
    taskIndex: number,
    zoneId: string | null
  ) => void;
  onEditFT: (ft: FindingType) => void;
  onTaskDragEnd: (ft: FindingType, event: DragEndEvent) => void;
  sensors: ReturnType<typeof useSensors>;
  mutating: boolean;
}

function FindingTypeAccordion({
  ft,
  isLinked,
  isTemplateReadOnly,
  zones,
  onToggleLink,
  onAddTask,
  onRemoveTask,
  onEditTask,
  onZoneChangeTask,
  onEditFT,
  onTaskDragEnd,
  sensors,
  mutating,
}: FindingTypeAccordionProps) {
  const tasks = [...(ft.tasks ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const editable = !ft.isSystem && !isTemplateReadOnly;

  const renderTasks = () => {
    if (tasks.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={(theme) => ({ py: 1, color: theme.custom?.textLight })}
        >
          No tasks defined.
        </Typography>
      );
    }

    if (!editable) {
      return (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            borderRadius: '8px',
            overflow: 'hidden',
            borderColor: theme.custom?.stroke,
            backgroundColor: theme.custom?.background1,
          })}
        >
          {tasks.map((task, idx) => (
            <Box
              key={task.id ?? idx}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderBottom: `1px solid ${theme.custom?.stroke}`,
                '&:last-child': { borderBottom: 'none' },
              })}
            >
              <Typography
                sx={(theme) => ({
                  fontSize: 14,
                  color: theme.custom?.text,
                  flex: 1,
                })}
              >
                {task.name}
              </Typography>
              <Chip
                label={task.taskType}
                size="small"
                variant="outlined"
                sx={(theme) => ({
                  color: theme.custom?.textLight,
                  borderColor: theme.custom?.stroke,
                })}
              />
            </Box>
          ))}
        </Paper>
      );
    }

    const sortableIds = tasks.map((_, i) => `${ft.id}-task-${i}`);
    return (
      <Paper
        variant="outlined"
        sx={(theme) => ({
          borderRadius: '8px',
          overflow: 'hidden',
          borderColor: theme.custom?.stroke,
          backgroundColor: theme.custom?.background1,
        })}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => onTaskDragEnd(ft, event)}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            {tasks.map((task, idx) => (
              <SortableFTTaskRow
                key={task.id ?? idx}
                task={task}
                sortableId={`${ft.id}-task-${idx}`}
                zones={zones}
                onRemove={() => onRemoveTask(ft, idx)}
                onEdit={() => onEditTask(ft, idx)}
                onZoneChange={(zoneId) => onZoneChangeTask(ft, idx, zoneId)}
                disabled={mutating}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Paper>
    );
  };

  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={false}
      sx={(theme) => ({
        backgroundColor: theme.custom?.background1,
      })}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" gap={1} flex={1}>
          <Typography
            sx={(theme) => ({ fontWeight: 500, color: theme.custom?.text })}
          >
            {ft.name}
          </Typography>
          {isLinked && (
            <Chip
              label="Linked"
              size="small"
              color="success"
              variant="outlined"
              icon={<LinkIcon sx={{ fontSize: 14 }} />}
            />
          )}
          <Typography
            variant="body2"
            sx={(theme) => ({
              ml: 'auto',
              mr: 1,
              color: theme.custom?.textLight,
            })}
          >
            {tasks.length} task{tasks.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {ft.description && (
          <Typography
            variant="body2"
            sx={(theme) => ({ mb: 1, color: theme.custom?.textLight })}
          >
            {ft.description}
          </Typography>
        )}

        {/* Action buttons */}
        <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
          {!isTemplateReadOnly && (
            <Button
              size="small"
              variant={isLinked ? 'outlined' : 'contained'}
              color={isLinked ? 'error' : 'primary'}
              startIcon={isLinked ? <LinkOffIcon /> : <LinkIcon />}
              onClick={() => onToggleLink(ft.id, isLinked)}
              disabled={mutating}
            >
              {isLinked ? 'Remove from Template' : 'Add to Template'}
            </Button>
          )}
          {editable && (
            <>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => onEditFT(ft)}
                disabled={mutating}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAddTask(ft)}
                disabled={mutating}
              >
                Add Task
              </Button>
            </>
          )}
        </Box>

        {renderTasks()}
      </AccordionDetails>
    </Accordion>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════════════════════

function TemplateEditor({
  templates,
  findingTypes,
  zones,
  onRefreshTemplates,
  onRefreshFindingTypes,
  onRefreshZones,
  isPaivcamSeed = false,
  initialTemplateId = null,
  onBackToList,
}: TemplateEditorProps) {
  const {
    cloneTemplate,
    updateTemplateItems,
    createFindingType,
    updateFindingType,
  } = useTemplateMutations();

  // ── View state ──
  const [viewingTemplateId, setViewingTemplateId] = useState<string | null>(
    initialTemplateId ?? null
  );
  const [subTab, setSubTab] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  // ── Clone dialog ──
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);

  // ── Snackbar ──
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ── Checklist tab state ──
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState<NewTaskForm>(EMPTY_TASK_FORM);
  const [mutating, setMutating] = useState(false);

  // ── Edit template item dialog ──
  const [editingItem, setEditingItem] = useState<TemplateItem | null>(null);
  const [editItemForm, setEditItemForm] = useState<{
    name: string;
    description: string;
    taskType: string;
    zoneId: string;
  }>({
    name: '',
    description: '',
    taskType: 'photo',
    zoneId: '',
  });

  // ── Rename template dialog ──
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');

  // ── Finding type inline create dialog ──
  const [createFTDialogOpen, setCreateFTDialogOpen] = useState(false);
  const [newFT, setNewFT] = useState<FTFormData>(EMPTY_FT_FORM);
  const [createFTCategory, setCreateFTCategory] = useState<
    'damage' | 'attribute'
  >('damage');

  // ── Finding type edit dialog ──
  const [editingFT, setEditingFT] = useState<FindingType | null>(null);
  const [editFTForm, setEditFTForm] = useState<FTFormData>(EMPTY_FT_FORM);

  // ── Finding type add-task dialog ──
  const [addFTTaskDialogOpen, setAddFTTaskDialogOpen] = useState(false);
  const [addFTTaskTarget, setAddFTTaskTarget] = useState<FindingType | null>(
    null
  );
  const [newFTTask, setNewFTTask] = useState<NewFTTaskForm>(EMPTY_FT_TASK_FORM);

  // ── Finding type edit-task dialog ──
  const [editFTTaskDialogOpen, setEditFTTaskDialogOpen] = useState(false);
  const [editFTTaskTarget, setEditFTTaskTarget] = useState<FindingType | null>(
    null
  );
  const [editFTTaskIndex, setEditFTTaskIndex] = useState<number>(-1);
  const [editFTTaskForm, setEditFTTaskForm] =
    useState<NewFTTaskForm>(EMPTY_FT_TASK_FORM);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // ── Derived data ──

  const sortedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => {
      const rank = (t: Template) => {
        if (t.status === 'published') return 0;
        return 1; // unpublished, draft, archived all treated as unpublished
      };
      return rank(a) - rank(b);
    });
  }, [templates]);

  const activeTemplate = useMemo(() => {
    if (!viewingTemplateId) return null;
    return templates.find((t) => t.id === viewingTemplateId) ?? null;
  }, [templates, viewingTemplateId]);

  const isSystemOnly = activeTemplate?.isSystem === true;
  const isReadOnly = isSystemOnly && !isPaivcamSeed;

  const groupedItems = useMemo(() => {
    if (!activeTemplate) return new Map<string, TemplateItem[]>();
    const items = (activeTemplate.items ?? []).filter((i) => !i.isDeleted);
    const groups = new Map<string, TemplateItem[]>();
    const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const item of sorted) {
      const key = item.zoneId ?? '__unassigned__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(item);
    }
    return groups;
  }, [activeTemplate]);

  const zoneMap = useMemo(() => {
    const map = new Map<string, Zone>();
    for (const z of zones) map.set(z.id, z);
    return map;
  }, [zones]);

  const sortedZoneKeys = useMemo(() => {
    return Array.from(groupedItems.keys()).sort((a, b) => {
      if (a === '__unassigned__') return 1;
      if (b === '__unassigned__') return -1;
      return (
        (zoneMap.get(a)?.sortOrder ?? 999999) -
        (zoneMap.get(b)?.sortOrder ?? 999999)
      );
    });
  }, [groupedItems, zoneMap]);

  const templateFindingTypeIds = useMemo(() => {
    if (!activeTemplate) return new Set<string>();
    return new Set(
      (activeTemplate.findingTypes ?? []).map(
        (ft: any) => ft.id ?? ft.findingTypeId
      )
    );
  }, [activeTemplate]);

  const totalItemCount = useMemo(() => {
    if (!activeTemplate) return 0;
    return (activeTemplate.items ?? []).filter((i) => !i.isDeleted).length;
  }, [activeTemplate]);

  // Finding types split by category
  // For non-paivcam-seed orgs, hide system finding types — they only work with their own
  const visibleFindingTypes = useMemo(
    () =>
      isPaivcamSeed ? findingTypes : findingTypes.filter((ft) => !ft.isSystem),
    [findingTypes, isPaivcamSeed]
  );
  // For non-paivcam-seed orgs, hide system zones — they only work with their own
  const visibleZones = useMemo(
    () => (isPaivcamSeed ? zones : zones.filter((z) => !z.isSystem)),
    [zones, isPaivcamSeed]
  );
  const damageTypes = useMemo(
    () =>
      visibleFindingTypes.filter(
        (ft) => (ft.category ?? 'damage') === 'damage'
      ),
    [visibleFindingTypes]
  );
  const roofFeatures = useMemo(
    () => visibleFindingTypes.filter((ft) => ft.category === 'attribute'),
    [visibleFindingTypes]
  );

  // Split linked / available for a given category list
  const splitLinkedAvailable = useCallback(
    (ftList: FindingType[]) => {
      const linked: FindingType[] = [];
      const available: FindingType[] = [];
      for (const ft of ftList) {
        if (templateFindingTypeIds.has(ft.id)) linked.push(ft);
        else available.push(ft);
      }
      return { linked, available };
    },
    [templateFindingTypeIds]
  );

  // ════════════════════════════════════════════════════════════════════════
  // Handlers — Template clone
  // ════════════════════════════════════════════════════════════════════════

  const handleCloneFromDialog = async () => {
    const sourceId = cloneSourceId;
    if (!sourceId) return;
    setCloning(true);
    try {
      const result = await cloneTemplate(sourceId);
      if (result?.success) {
        showSnackbar('Template created successfully', 'success');
        setCloneDialogOpen(false);
        setCloneSourceId(null);
        onRefreshTemplates();
        onRefreshFindingTypes();
        onRefreshZones();
      } else {
        showSnackbar(result?.error ?? 'Failed to create template', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to create template', 'error');
    } finally {
      setCloning(false);
    }
  };

  const handleClone = async () => {
    if (!activeTemplate) return;
    setCloning(true);
    try {
      const result = await cloneTemplate(activeTemplate.id);
      if (result?.success) {
        showSnackbar('Template cloned successfully', 'success');
        setViewingTemplateId(null);
        onRefreshTemplates();
        onRefreshFindingTypes();
        onRefreshZones();
      } else {
        showSnackbar(result?.error ?? 'Failed to clone template', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to clone template', 'error');
    } finally {
      setCloning(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // Handlers — Checklist tab (template items)
  // ════════════════════════════════════════════════════════════════════════

  const handleAddTask = async () => {
    if (!activeTemplate || !newTask.name.trim()) return;
    setMutating(true);
    try {
      // Calculate sortOrder so the new task is placed last in its zone
      const zoneKey = newTask.zoneId || '__unassigned__';
      const zoneItems = groupedItems.get(zoneKey) ?? [];
      const maxSortOrder =
        zoneItems.length > 0
          ? Math.max(...zoneItems.map((i) => i.sortOrder))
          : 0;
      const result = await updateTemplateItems(activeTemplate.id, [
        {
          action: 'add',
          item: {
            name: newTask.name.trim(),
            description: newTask.description.trim() || null,
            taskType: newTask.taskType,
            zoneId: newTask.zoneId || undefined,
            sortOrder: maxSortOrder + 10,
          },
        },
      ]);
      if (result?.success) {
        showSnackbar('Task added', 'success');
        setNewTask(EMPTY_TASK_FORM);
        setAddTaskOpen(false);
        onRefreshTemplates();
      } else {
        showSnackbar(result?.error ?? 'Failed to add task', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to add task', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleRemoveTask = async (itemId: string) => {
    if (!activeTemplate) return;
    setMutating(true);
    try {
      const result = await updateTemplateItems(activeTemplate.id, [
        { action: 'remove', itemId },
      ]);
      if (result?.success) {
        showSnackbar('Task removed', 'success');
        onRefreshTemplates();
      } else {
        showSnackbar(result?.error ?? 'Failed to remove task', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to remove task', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleAssignZone = async (itemId: string, zoneId: string) => {
    if (!activeTemplate) return;
    setMutating(true);
    try {
      const result = await updateTemplateItems(activeTemplate.id, [
        { action: 'assignZone', itemId, zoneId: zoneId || null },
      ]);
      if (result?.success) {
        onRefreshTemplates();
      } else {
        showSnackbar(result?.error ?? 'Failed to assign zone', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to assign zone', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleItemDragEnd = useCallback(
    async (zoneKey: string, event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !activeTemplate) return;
      const zoneItems = groupedItems.get(zoneKey) ?? [];
      const oldIndex = zoneItems.findIndex((i) => i.id === active.id);
      const newIndex = zoneItems.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove([...zoneItems], oldIndex, newIndex);
      setMutating(true);
      try {
        const result = await updateTemplateItems(activeTemplate.id, [
          {
            action: 'reorder',
            items: reordered.map((item, i) => ({
              id: item.id,
              sortOrder: i * 10,
            })),
          },
        ]);
        if (result?.success) {
          onRefreshTemplates();
        } else {
          showSnackbar(result?.error ?? 'Failed to reorder', 'error');
        }
      } catch (err: any) {
        showSnackbar(err?.message ?? 'Failed to reorder', 'error');
      } finally {
        setMutating(false);
      }
    },
    [activeTemplate, groupedItems, updateTemplateItems, onRefreshTemplates]
  );

  // ════════════════════════════════════════════════════════════════════════
  // Handlers — Edit template item
  // ════════════════════════════════════════════════════════════════════════

  const handleStartEditItem = (item: TemplateItem) => {
    setEditingItem(item);
    setEditItemForm({
      name: item.name,
      description: item.description ?? '',
      taskType: item.taskType,
      zoneId: item.zoneId ?? '',
    });
  };

  const handleSaveEditItem = async () => {
    if (!activeTemplate || !editingItem || !editItemForm.name.trim()) return;
    setMutating(true);
    try {
      const result = await updateTemplateItems(activeTemplate.id, [
        {
          action: 'update',
          itemId: editingItem.id,
          updates: {
            name: editItemForm.name.trim(),
            description: editItemForm.description.trim() || null,
            taskType: editItemForm.taskType,
            zoneId: editItemForm.zoneId || null,
          },
        },
      ]);
      if (result?.success) {
        showSnackbar('Task updated', 'success');
        setEditingItem(null);
        onRefreshTemplates();
      } else {
        showSnackbar(result?.error ?? 'Failed to update task', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update task', 'error');
    } finally {
      setMutating(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // Handlers — Rename template
  // ════════════════════════════════════════════════════════════════════════

  const handleOpenRename = () => {
    if (!activeTemplate) return;
    setRenameName(activeTemplate.name);
    setRenameOpen(true);
  };

  const handleSaveRename = async () => {
    if (!activeTemplate || !renameName.trim()) return;
    setMutating(true);
    try {
      const result = await updateTemplateItems(activeTemplate.id, [
        {
          action: 'renameTemplate',
          name: renameName.trim(),
        },
      ]);
      if (result?.success) {
        showSnackbar('Template renamed', 'success');
        setRenameOpen(false);
        onRefreshTemplates();
      } else {
        showSnackbar(result?.error ?? 'Failed to rename', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to rename', 'error');
    } finally {
      setMutating(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // Handlers — Finding type toggle (junction table)
  // ════════════════════════════════════════════════════════════════════════

  const handleToggleFindingType = async (
    findingTypeId: string,
    isLinked: boolean
  ) => {
    if (!activeTemplate) return;
    setMutating(true);
    try {
      const result = await updateTemplateItems(activeTemplate.id, [
        {
          action: isLinked ? 'removeFindingType' : 'addFindingType',
          findingTypeId,
        },
      ]);
      if (result?.success) {
        showSnackbar(
          isLinked
            ? 'Finding type removed from template'
            : 'Finding type added to template',
          'success'
        );
        onRefreshTemplates();
      } else {
        showSnackbar(result?.error ?? 'Failed to update', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update', 'error');
    } finally {
      setMutating(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // Handlers — Inline finding type CRUD (for Damage Types / Property Features)
  // ════════════════════════════════════════════════════════════════════════

  const handleCreateFT = async () => {
    if (!newFT.name.trim()) return;
    setMutating(true);
    try {
      const result = await createFindingType({
        name: newFT.name.trim(),
        code: generateCode(newFT.name),
        description: newFT.description.trim() || undefined,
        category: createFTCategory,
        tasks: [],
      });
      if (result?.success) {
        showSnackbar(
          `${
            createFTCategory === 'damage' ? 'Damage type' : 'Property feature'
          } created`,
          'success'
        );
        setNewFT(EMPTY_FT_FORM);
        setCreateFTDialogOpen(false);
        onRefreshFindingTypes();
      } else {
        showSnackbar(result?.error ?? 'Failed to create', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to create', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleStartEditFT = (ft: FindingType) => {
    setEditingFT(ft);
    setEditFTForm({ name: ft.name, description: ft.description ?? '' });
  };

  const handleSaveEditFT = async () => {
    if (!editingFT || !editFTForm.name.trim()) return;
    setMutating(true);
    try {
      const result = await updateFindingType(editingFT.id, {
        name: editFTForm.name.trim(),
        description: editFTForm.description.trim() || undefined,
      });
      if (result?.success) {
        showSnackbar('Finding type updated', 'success');
        setEditingFT(null);
        onRefreshFindingTypes();
      } else {
        showSnackbar(result?.error ?? 'Failed to update', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleOpenAddFTTask = (ft: FindingType) => {
    setAddFTTaskTarget(ft);
    setNewFTTask(EMPTY_FT_TASK_FORM);
    setAddFTTaskDialogOpen(true);
  };

  const handleAddFTTask = async () => {
    if (!addFTTaskTarget || !newFTTask.name.trim()) return;
    setMutating(true);
    try {
      const existingTasks = (addFTTaskTarget.tasks ?? []).map((t) => ({
        taskType: t.taskType,
        itemKey: t.itemKey,
        name: t.name,
        description: t.description,
        sortOrder: t.sortOrder,
        zoneId: t.zoneId,
      }));
      const nextSortOrder =
        existingTasks.length > 0
          ? Math.max(...existingTasks.map((t) => t.sortOrder)) + 10
          : 10;
      const updatedTasks = [
        ...existingTasks,
        {
          taskType: newFTTask.taskType,
          itemKey: generateCode(newFTTask.name),
          name: newFTTask.name.trim(),
          description: newFTTask.description.trim() || null,
          sortOrder: nextSortOrder,
          zoneId: newFTTask.zoneId || undefined,
        },
      ];
      const result = await updateFindingType(addFTTaskTarget.id, {
        tasks: updatedTasks,
      });
      if (result?.success) {
        showSnackbar('Task added to finding type', 'success');
        setAddFTTaskDialogOpen(false);
        setAddFTTaskTarget(null);
        onRefreshFindingTypes();
      } else {
        showSnackbar(result?.error ?? 'Failed to add task', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to add task', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleRemoveFTTask = async (ft: FindingType, taskIndex: number) => {
    setMutating(true);
    try {
      const updatedTasks = (ft.tasks ?? [])
        .filter((_, i) => i !== taskIndex)
        .map((t, i) => ({
          taskType: t.taskType,
          itemKey: t.itemKey,
          name: t.name,
          sortOrder: i * 10,
          zoneId: t.zoneId,
        }));
      const result = await updateFindingType(ft.id, { tasks: updatedTasks });
      if (result?.success) {
        showSnackbar('Task removed', 'success');
        onRefreshFindingTypes();
      } else {
        showSnackbar(result?.error ?? 'Failed to remove task', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to remove task', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleStartEditFTTask = (ft: FindingType, taskIndex: number) => {
    const tasks = [...(ft.tasks ?? [])].sort(
      (a, b) => a.sortOrder - b.sortOrder
    );
    const task = tasks[taskIndex];
    if (!task) return;
    setEditFTTaskTarget(ft);
    setEditFTTaskIndex(taskIndex);
    setEditFTTaskForm({
      name: task.name,
      description: task.description ?? '',
      taskType: task.taskType,
      zoneId: task.zoneId ?? '',
    });
    setEditFTTaskDialogOpen(true);
  };

  const handleSaveEditFTTask = async () => {
    if (!editFTTaskTarget || editFTTaskIndex < 0 || !editFTTaskForm.name.trim())
      return;
    setMutating(true);
    try {
      const sortedTasks = [...(editFTTaskTarget.tasks ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder
      );
      const updatedTasks = sortedTasks.map((t, i) => {
        const base = {
          taskType: t.taskType,
          itemKey: t.itemKey,
          name: t.name,
          description: t.description,
          sortOrder: i * 10,
          zoneId: t.zoneId,
        };
        if (i === editFTTaskIndex) {
          return {
            ...base,
            name: editFTTaskForm.name.trim(),
            description: editFTTaskForm.description.trim() || null,
            taskType: editFTTaskForm.taskType,
            zoneId: editFTTaskForm.zoneId || undefined,
          };
        }
        return base;
      });
      const result = await updateFindingType(editFTTaskTarget.id, {
        tasks: updatedTasks,
      });
      if (result?.success) {
        showSnackbar('Task updated', 'success');
        setEditFTTaskDialogOpen(false);
        setEditFTTaskTarget(null);
        onRefreshFindingTypes();
      } else {
        showSnackbar(result?.error ?? 'Failed to update task', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update task', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleFTTaskZoneChange = async (
    ft: FindingType,
    taskIndex: number,
    zoneId: string | null
  ) => {
    setMutating(true);
    try {
      const sortedTasks = [...(ft.tasks ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder
      );
      const updatedTasks = sortedTasks.map((t, i) => {
        const base = {
          taskType: t.taskType,
          itemKey: t.itemKey,
          name: t.name,
          sortOrder: i * 10,
          zoneId: t.zoneId,
        };
        if (i === taskIndex) {
          return { ...base, zoneId: zoneId || undefined };
        }
        return base;
      });
      const result = await updateFindingType(ft.id, { tasks: updatedTasks });
      if (result?.success) {
        onRefreshFindingTypes();
      } else {
        showSnackbar(result?.error ?? 'Failed to update zone', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update zone', 'error');
    } finally {
      setMutating(false);
    }
  };

  const handleFTTaskDragEnd = useCallback(
    async (ft: FindingType, event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const tasks = [...(ft.tasks ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder
      );
      const oldIndex = tasks.findIndex(
        (_, i) => `${ft.id}-task-${i}` === active.id
      );
      const newIndex = tasks.findIndex(
        (_, i) => `${ft.id}-task-${i}` === over.id
      );
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(tasks, oldIndex, newIndex);
      const updatedTasks = reordered.map((t, i) => ({
        taskType: t.taskType,
        itemKey: t.itemKey,
        name: t.name,
        sortOrder: i * 10,
        zoneId: t.zoneId,
      }));
      setMutating(true);
      try {
        const result = await updateFindingType(ft.id, { tasks: updatedTasks });
        if (result?.success) {
          onRefreshFindingTypes();
        } else {
          showSnackbar(result?.error ?? 'Failed to reorder tasks', 'error');
        }
      } catch (err: any) {
        showSnackbar(err?.message ?? 'Failed to reorder tasks', 'error');
      } finally {
        setMutating(false);
      }
    },
    [updateFindingType, onRefreshFindingTypes]
  );

  // ── Navigate back to list ──
  const handleBackToList = () => {
    if (onBackToList) {
      onBackToList();
    } else {
      setViewingTemplateId(null);
    }
    setSubTab(0);
  };

  // ════════════════════════════════════════════════════════════════════════
  // Render helper — Finding type section (Damage Types or Property Features)
  // ════════════════════════════════════════════════════════════════════════

  const renderFindingTypeTab = (
    ftList: FindingType[],
    categoryKey: 'damage' | 'attribute',
    label: string,
    description: string
  ) => {
    const { linked, available } = splitLinkedAvailable(ftList);

    return (
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box flex={1} mr={2}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.custom?.textLight })}
            >
              {description}
            </Typography>
          </Box>
          {!isReadOnly && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewFT(EMPTY_FT_FORM);
                setCreateFTCategory(categoryKey);
                setCreateFTDialogOpen(true);
              }}
              disabled={mutating}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              New{' '}
              {categoryKey === 'damage' ? 'Damage Type' : 'Property Feature'}
            </Button>
          )}
        </Box>

        {/* Linked to this template */}
        <Typography
          variant="subtitle2"
          sx={(theme) => ({
            color: theme.custom?.text,
            mb: 1,
            fontWeight: 600,
          })}
        >
          Linked to this template ({linked.length})
        </Typography>
        {linked.length === 0 ? (
          <Typography
            variant="body2"
            sx={(theme) => ({ mb: 2, color: theme.custom?.textLight })}
          >
            No {label.toLowerCase()} linked yet. Add some from the list below.
          </Typography>
        ) : (
          <Box sx={{ mb: 2 }}>
            {linked.map((ft) => (
              <FindingTypeAccordion
                key={ft.id}
                ft={ft}
                isLinked={true}
                isTemplateReadOnly={isReadOnly}
                zones={visibleZones}
                onToggleLink={handleToggleFindingType}
                onAddTask={handleOpenAddFTTask}
                onRemoveTask={handleRemoveFTTask}
                onEditTask={handleStartEditFTTask}
                onZoneChangeTask={handleFTTaskZoneChange}
                onEditFT={handleStartEditFT}
                onTaskDragEnd={handleFTTaskDragEnd}
                sensors={sensors}
                mutating={mutating}
              />
            ))}
          </Box>
        )}

        {/* Available to add */}
        <Typography
          variant="subtitle2"
          sx={(theme) => ({
            color: theme.custom?.text,
            mb: 1,
            fontWeight: 600,
          })}
        >
          Available to add ({available.length})
        </Typography>
        {available.length === 0 ? (
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
          >
            All {label.toLowerCase()} are already linked to this template.
          </Typography>
        ) : (
          <Box>
            {available.map((ft) => (
              <FindingTypeAccordion
                key={ft.id}
                ft={ft}
                isLinked={false}
                isTemplateReadOnly={isReadOnly}
                zones={visibleZones}
                onToggleLink={handleToggleFindingType}
                onAddTask={handleOpenAddFTTask}
                onRemoveTask={handleRemoveFTTask}
                onEditTask={handleStartEditFTTask}
                onZoneChangeTask={handleFTTaskZoneChange}
                onEditFT={handleStartEditFT}
                onTaskDragEnd={handleFTTaskDragEnd}
                sensors={sensors}
                mutating={mutating}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // LIST VIEW — show all templates as clickable rows
  // ════════════════════════════════════════════════════════════════════════════

  if (!viewingTemplateId) {
    return (
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">Templates</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setCloneSourceId(null);
              setCloneDialogOpen(true);
            }}
          >
            Create Template
          </Button>
        </Box>

        {sortedTemplates.length === 0 ? (
          <Typography sx={(theme) => ({ color: theme.custom?.textLight })}>
            No templates available.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sortedTemplates.map((t) => {
              const displayStatus = t.isSystem
                ? 'system'
                : getDisplayStatus(t.status);
              const itemCount = (t.items ?? []).filter(
                (i) => !i.isDeleted
              ).length;
              return (
                <Paper
                  key={t.id}
                  variant="outlined"
                  onClick={() => {
                    setViewingTemplateId(t.id);
                    setSubTab(0);
                  }}
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    px: 2.5,
                    py: 2,
                    cursor: 'pointer',
                    borderRadius: '10px',
                    borderColor: theme.custom?.stroke,
                    backgroundColor: theme.custom?.background1,
                    '&:hover': {
                      borderColor: theme.custom?.primary,
                      backgroundColor: theme.custom?.background2,
                    },
                  })}
                >
                  {t.isSystem && (
                    <LockIcon
                      sx={(theme) => ({
                        fontSize: 18,
                        color: theme.custom?.textLight,
                        mr: 1.5,
                      })}
                    />
                  )}
                  <Box flex={1}>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 15,
                        fontWeight: 600,
                        color: theme.custom?.text,
                      })}
                    >
                      {t.name}
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 13,
                        color: theme.custom?.textLight,
                        mt: 0.25,
                      })}
                    >
                      {itemCount} tasks · v{t.version}
                      {t.description ? ` · ${t.description}` : ''}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={displayStatus}
                      color={getStatusColor(t.isSystem ? 'system' : t.status)}
                      size="small"
                    />
                    <ChevronRightIcon
                      sx={(theme) => ({
                        color: theme.custom?.textLight,
                      })}
                    />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Create Template Dialog — select a template to clone */}
        <Dialog
          open={cloneDialogOpen}
          onClose={() => setCloneDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={dialogPaperProps}
        >
          <DialogTitle>Create Template</DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              sx={(theme) => ({ mb: 2, color: theme.custom?.textLight })}
            >
              Select an existing template to use as a starting point. A new
              draft will be created with all its tasks, zones, and finding
              types.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {templates.map((t) => {
                const isSelected = cloneSourceId === t.id;
                const displayStatus = t.isSystem
                  ? 'system'
                  : getDisplayStatus(t.status);
                const itemCount = (t.items ?? []).filter(
                  (i) => !i.isDeleted
                ).length;
                return (
                  <Paper
                    key={t.id}
                    variant="outlined"
                    onClick={() => setCloneSourceId(t.id)}
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      borderRadius: '10px',
                      borderColor: isSelected
                        ? theme.custom?.primary
                        : theme.custom?.stroke,
                      borderWidth: isSelected ? 2 : 1,
                      backgroundColor: isSelected
                        ? theme.custom?.isDark
                          ? 'rgba(255, 106, 0, 0.15)'
                          : '#FFF3E0'
                        : theme.custom?.background1,
                      '&:hover': {
                        borderColor: theme.custom?.primary,
                      },
                    })}
                  >
                    <Box flex={1}>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 14,
                          fontWeight: 500,
                          color: theme.custom?.text,
                        })}
                      >
                        {t.name}
                      </Typography>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.textLight,
                        })}
                      >
                        {itemCount} tasks · v{t.version}
                      </Typography>
                    </Box>
                    <Chip
                      label={displayStatus}
                      color={getStatusColor(displayStatus)}
                      size="small"
                    />
                  </Paper>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCloneFromDialog}
              variant="contained"
              disabled={!cloneSourceId || cloning}
            >
              {cloning ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // DETAIL VIEW — template header + sub-tabs
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <Box>
      {/* Back button + breadcrumb */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <IconButton
          onClick={handleBackToList}
          size="small"
          aria-label="Back to templates"
        >
          <ArrowBackIcon sx={(theme) => ({ color: theme.custom?.textLight })} />
        </IconButton>
        <Typography
          sx={(theme) => ({
            fontSize: 13,
            color: theme.custom?.textLight,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          })}
          onClick={handleBackToList}
        >
          Templates
        </Typography>
      </Box>

      {activeTemplate && (
        <>
          {/* Template header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography
                variant="h6"
                onClick={!isReadOnly ? handleOpenRename : undefined}
                sx={(theme) => ({
                  color: theme.custom?.text,
                  ...(isReadOnly
                    ? {}
                    : {
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }),
                })}
              >
                {activeTemplate.name}
              </Typography>
              {!isReadOnly && (
                <IconButton
                  size="small"
                  onClick={handleOpenRename}
                  aria-label="Rename template"
                >
                  <EditIcon
                    sx={(theme) => ({
                      fontSize: 18,
                      color: theme.custom?.textLight,
                    })}
                  />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => setHelpOpen(true)}
                aria-label="Help"
              >
                <HelpOutlineIcon
                  sx={(theme) => ({ color: theme.custom?.textLight })}
                />
              </IconButton>
              <Chip
                label={
                  isSystemOnly
                    ? 'System'
                    : getDisplayStatus(activeTemplate.status)
                }
                color={getStatusColor(
                  isSystemOnly ? 'system' : activeTemplate.status
                )}
                size="small"
              />
              <Typography
                sx={(theme) => ({
                  fontSize: 13,
                  color: theme.custom?.textLight,
                })}
              >
                {totalItemCount} tasks
              </Typography>
              {isSystemOnly && !isPaivcamSeed && (
                <LockIcon
                  sx={(theme) => ({
                    fontSize: 18,
                    color: theme.custom?.textLight,
                  })}
                />
              )}
            </Box>
            <Box display="flex" gap={1}>
              {(!isSystemOnly || isPaivcamSeed) && (
                <TemplatePublishingFlow
                  template={activeTemplate}
                  onRefresh={onRefreshTemplates}
                />
              )}
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleClone}
                disabled={cloning}
                size="small"
              >
                {cloning ? 'Cloning…' : 'Clone'}
              </Button>
            </Box>
          </Box>

          {/* Info alert */}
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            {isSystemOnly && !isPaivcamSeed
              ? 'This is the default system template. Clone it to create a customizable version for your organization.'
              : isSystemOnly && isPaivcamSeed
              ? 'You are editing the system template as paivcam-seed. Changes will be visible to all organizations.'
              : "Your organization can have one published template at a time. Unpublished templates won't affect inspections until published."}
          </Alert>

          {/* Sub-tabs — show full editor for org templates and paivcam-seed on system templates */}
          {isSystemOnly && !isPaivcamSeed ? (
            // System template for non-paivcam-seed: read-only checklist only, no tabs
            <Box>
              {sortedZoneKeys.map((zoneKey) => (
                <ZoneGroup
                  key={zoneKey}
                  zoneKey={zoneKey}
                  zoneName={zoneMap.get(zoneKey)?.name ?? 'Unassigned'}
                  items={groupedItems.get(zoneKey) ?? []}
                  zones={visibleZones}
                  sensors={sensors}
                  onItemDragEnd={handleItemDragEnd}
                  onAssignZone={handleAssignZone}
                  onRemove={handleRemoveTask}
                  onEditItem={handleStartEditItem}
                  disabled={mutating}
                  isReadOnly={true}
                />
              ))}
              {groupedItems.size === 0 && (
                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    p: 3,
                    borderRadius: '10px',
                    textAlign: 'center',
                    borderColor: theme.custom?.stroke,
                    backgroundColor: theme.custom?.background1,
                  })}
                >
                  <Typography
                    sx={(theme) => ({ color: theme.custom?.textLight })}
                  >
                    No tasks in this template.
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : (
            // Org-owned template: sub-tabs
            <>
              <Tabs
                value={subTab}
                onChange={(_, v) => setSubTab(v)}
                sx={(theme) => ({
                  mb: 2,
                  borderBottom: `1px solid ${theme.custom?.stroke}`,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: 14,
                    color: theme.custom?.textLight,
                    '&.Mui-selected': {
                      color: theme.custom?.primary,
                      fontWeight: 600,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.custom?.primary,
                  },
                })}
              >
                <Tab label="Checklist" />
                <Tab label="Damage Types" />
                <Tab label="Property Features" />
                <Tab label="Zones" />
              </Tabs>

              {/* ── Tab 0: Checklist ── */}
              {subTab === 0 && (
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography
                      variant="body2"
                      sx={(theme) => ({
                        flex: 1,
                        mr: 2,
                        color: theme.custom?.textLight,
                      })}
                    >
                      These are the standard tasks every inspector completes
                      during an inspection — things like property photos, roof
                      material close-ups, and condition notes. Tasks are grouped
                      by zone and completed in order.
                    </Typography>
                    {!isReadOnly && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setAddTaskOpen(true)}
                        disabled={mutating}
                        sx={{ flexShrink: 0 }}
                      >
                        Add Task
                      </Button>
                    )}
                  </Box>

                  {sortedZoneKeys.map((zoneKey) => (
                    <ZoneGroup
                      key={zoneKey}
                      zoneKey={zoneKey}
                      zoneName={zoneMap.get(zoneKey)?.name ?? 'Unassigned'}
                      items={groupedItems.get(zoneKey) ?? []}
                      zones={visibleZones}
                      sensors={sensors}
                      onItemDragEnd={handleItemDragEnd}
                      onAssignZone={handleAssignZone}
                      onRemove={handleRemoveTask}
                      onEditItem={handleStartEditItem}
                      disabled={mutating}
                      isReadOnly={isReadOnly}
                    />
                  ))}

                  {groupedItems.size === 0 && (
                    <Paper
                      variant="outlined"
                      sx={(theme) => ({
                        p: 3,
                        borderRadius: '10px',
                        textAlign: 'center',
                        borderColor: theme.custom?.stroke,
                        backgroundColor: theme.custom?.background1,
                      })}
                    >
                      <Typography
                        sx={(theme) => ({ color: theme.custom?.textLight })}
                      >
                        No tasks in this template yet.
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {/* ── Tab 1: Damage Types ── */}
              {subTab === 1 &&
                renderFindingTypeTab(
                  damageTypes,
                  'damage',
                  'Damage Types',
                  'Damage types are what Hank matches when an inspector reports damage during an inspection. Use clear, descriptive names like "Hail Damage" or "Wind Damage" — Hank uses the name and description to match what inspectors say. Each damage type can have its own set of required tasks (photos, measurements, notes).'
                )}

              {/* ── Tab 2: Property Features ── */}
              {subTab === 2 &&
                renderFindingTypeTab(
                  roofFeatures,
                  'attribute',
                  'Property Features',
                  'Property features are structural elements or characteristics that may or may not be present on a property — like chimneys, skylights, or pipe jacks. Name them the way inspectors talk about them — Hank matches spoken descriptions to these names. Each feature can have required documentation tasks.'
                )}

              {/* ── Tab 3: Zones ── */}
              {subTab === 3 && (
                <ZoneEditor
                  zones={zones}
                  onRefresh={onRefreshZones}
                  isPaivcamSeed={isPaivcamSeed}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* Dialogs                                                          */}
      {/* ══════════════════════════════════════════════════════════════════ */}

      {/* Add Task Dialog (Checklist tab) */}
      <Dialog
        open={addTaskOpen}
        onClose={() => setAddTaskOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Add Task</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Task Name"
              value={newTask.name}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <StyledSelect
              value={newTask.taskType}
              onChange={(e) =>
                setNewTask((p) => ({
                  ...p,
                  taskType: e.target.value as string,
                }))
              }
              size="small"
              fullWidth
            >
              <StyledMenuItem value="photo">Photo</StyledMenuItem>
              <StyledMenuItem value="question">Question</StyledMenuItem>
            </StyledSelect>
            {newTask.taskType === 'question' && (
              <TextField
                label="Question"
                placeholder="e.g., What is the approximate roof age?"
                value={newTask.description ?? ''}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, description: e.target.value }))
                }
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            )}
            {newTask.taskType === 'photo' && (
              <TextField
                label="Description (optional)"
                value={newTask.description ?? ''}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, description: e.target.value }))
                }
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            )}
            <StyledSelect
              value={newTask.zoneId}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, zoneId: e.target.value as string }))
              }
              size="small"
              fullWidth
              displayEmpty
            >
              <StyledMenuItem value="">
                <em>No Zone</em>
              </StyledMenuItem>
              {visibleZones.map((z) => (
                <StyledMenuItem key={z.id} value={z.id}>
                  {z.name}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTaskOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddTask}
            variant="contained"
            disabled={!newTask.name.trim() || !newTask.zoneId || mutating}
          >
            {mutating ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Item Dialog (Checklist tab) */}
      <Dialog
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Task Name"
              value={editItemForm.name}
              onChange={(e) =>
                setEditItemForm((p) => ({ ...p, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <StyledSelect
              value={editItemForm.taskType}
              onChange={(e) =>
                setEditItemForm((p) => ({
                  ...p,
                  taskType: e.target.value as string,
                }))
              }
              size="small"
              fullWidth
            >
              <StyledMenuItem value="photo">Photo</StyledMenuItem>
              <StyledMenuItem value="question">Question</StyledMenuItem>
            </StyledSelect>
            <TextField
              label={
                editItemForm.taskType === 'question'
                  ? 'Question'
                  : 'Description (optional)'
              }
              placeholder={
                editItemForm.taskType === 'question'
                  ? 'e.g., What is the approximate roof age?'
                  : ''
              }
              value={editItemForm.description}
              onChange={(e) =>
                setEditItemForm((p) => ({ ...p, description: e.target.value }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <StyledSelect
              value={editItemForm.zoneId}
              onChange={(e) =>
                setEditItemForm((p) => ({
                  ...p,
                  zoneId: e.target.value as string,
                }))
              }
              size="small"
              fullWidth
              displayEmpty
            >
              <StyledMenuItem value="">
                <em>No Zone</em>
              </StyledMenuItem>
              {visibleZones.map((z) => (
                <StyledMenuItem key={z.id} value={z.id}>
                  {z.name}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingItem(null)}>Cancel</Button>
          <Button
            onClick={handleSaveEditItem}
            variant="contained"
            disabled={!editItemForm.name.trim() || mutating}
          >
            {mutating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Finding Type Dialog (Damage Types / Property Features tabs) */}
      <Dialog
        open={createFTDialogOpen}
        onClose={() => setCreateFTDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>
          Create{' '}
          {createFTCategory === 'damage' ? 'Damage Type' : 'Property Feature'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              value={newFT.name}
              onChange={(e) =>
                setNewFT((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Description (optional)"
              value={newFT.description}
              onChange={(e) =>
                setNewFT((prev) => ({ ...prev, description: e.target.value }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFTDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFT}
            variant="contained"
            disabled={!newFT.name.trim() || mutating}
          >
            {mutating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Finding Type Dialog */}
      <Dialog
        open={!!editingFT}
        onClose={() => setEditingFT(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Edit Finding Type</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              value={editFTForm.name}
              onChange={(e) =>
                setEditFTForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Description (optional)"
              value={editFTForm.description}
              onChange={(e) =>
                setEditFTForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFT(null)}>Cancel</Button>
          <Button
            onClick={handleSaveEditFT}
            variant="contained"
            disabled={!editFTForm.name.trim() || mutating}
          >
            {mutating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Task to Finding Type Dialog */}
      <Dialog
        open={addFTTaskDialogOpen}
        onClose={() => setAddFTTaskDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Add Task to {addFTTaskTarget?.name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Task Name"
              value={newFTTask.name}
              onChange={(e) =>
                setNewFTTask((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <StyledSelect
              value={newFTTask.taskType}
              onChange={(e) =>
                setNewFTTask((prev) => ({
                  ...prev,
                  taskType: e.target.value as string,
                }))
              }
              size="small"
              fullWidth
            >
              <StyledMenuItem value="photo">Photo</StyledMenuItem>
              <StyledMenuItem value="question">Question</StyledMenuItem>
            </StyledSelect>
            <TextField
              label={
                newFTTask.taskType === 'question'
                  ? 'Question'
                  : 'Description (optional)'
              }
              placeholder={
                newFTTask.taskType === 'question'
                  ? 'e.g., What is the approximate roof age?'
                  : ''
              }
              value={newFTTask.description}
              onChange={(e) =>
                setNewFTTask((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <StyledSelect
              value={newFTTask.zoneId}
              onChange={(e) =>
                setNewFTTask((prev) => ({
                  ...prev,
                  zoneId: e.target.value as string,
                }))
              }
              size="small"
              fullWidth
              displayEmpty
            >
              <StyledMenuItem value="">
                <em>No Zone</em>
              </StyledMenuItem>
              {visibleZones.map((z) => (
                <StyledMenuItem key={z.id} value={z.id}>
                  {z.name}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFTTaskDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddFTTask}
            variant="contained"
            disabled={!newFTTask.name.trim() || !newFTTask.zoneId || mutating}
          >
            {mutating ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Finding Type Task Dialog */}
      <Dialog
        open={editFTTaskDialogOpen}
        onClose={() => setEditFTTaskDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Task Name"
              value={editFTTaskForm.name}
              onChange={(e) =>
                setEditFTTaskForm((p) => ({ ...p, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <StyledSelect
              value={editFTTaskForm.taskType}
              onChange={(e) =>
                setEditFTTaskForm((p) => ({
                  ...p,
                  taskType: e.target.value as string,
                }))
              }
              size="small"
              fullWidth
            >
              <StyledMenuItem value="photo">Photo</StyledMenuItem>
              <StyledMenuItem value="question">Question</StyledMenuItem>
            </StyledSelect>
            <TextField
              label={
                editFTTaskForm.taskType === 'question'
                  ? 'Question'
                  : 'Description (optional)'
              }
              placeholder={
                editFTTaskForm.taskType === 'question'
                  ? 'e.g., What is the approximate roof age?'
                  : ''
              }
              value={editFTTaskForm.description}
              onChange={(e) =>
                setEditFTTaskForm((p) => ({
                  ...p,
                  description: e.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <StyledSelect
              value={editFTTaskForm.zoneId}
              onChange={(e) =>
                setEditFTTaskForm((p) => ({
                  ...p,
                  zoneId: e.target.value as string,
                }))
              }
              size="small"
              fullWidth
              displayEmpty
            >
              <StyledMenuItem value="">
                <em>No Zone</em>
              </StyledMenuItem>
              {visibleZones.map((z) => (
                <StyledMenuItem key={z.id} value={z.id}>
                  {z.name}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFTTaskDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEditFTTask}
            variant="contained"
            disabled={!editFTTaskForm.name.trim() || mutating}
          >
            {mutating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Template Dialog */}
      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Rename Template</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Template Name"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRename}
            variant="contained"
            disabled={!renameName.trim() || mutating}
          >
            {mutating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>How to Use This Template</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Checklist
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            The checklist is the ordered list of tasks every inspector completes
            — property photos, material close-ups, condition notes, etc. Tasks
            are grouped by zone and completed in order. Click any task to edit
            its name, description, or type.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Damage Types
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            Damage types are what Hank matches when an inspector verbally
            reports damage. Use clear, natural names like &quot;Hail
            Damage&quot; or &quot;Missing Shingles&quot; — Hank uses the name
            and description to match spoken words. Add detailed descriptions
            with keywords inspectors might say to improve matching accuracy.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Property Features
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            Property features are structural elements or characteristics that
            may or may not be present on a property — like chimneys, skylights,
            or pipe jacks. When an inspector mentions a feature, Hank matches it
            by name and description. Each feature can have required
            documentation tasks.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Zones
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            paragraph
          >
            Zones organize tasks by location — like &quot;Ground Level&quot; and
            &quot;Roof Level&quot;. Inspectors work through tasks zone by zone
            during an inspection.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Naming Tips for Hank
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
          >
            Hank uses trigram similarity to match what inspectors say to your
            damage types and property features. For best results: use plain
            English names (not codes), add descriptions with common synonyms and
            keywords, and test by saying the name out loud — if it sounds
            natural, Hank will match it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TemplateEditor;
